import React, { useState, useEffect } from 'react';
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import * as Excel from 'exceljs';
import { saveAs } from 'file-saver';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import useAuthStore from '../../stores/authStore';
import useUserProfileStore from '../../stores/userProfileStore';
import {
  SETTINGS_SECTION_KEYS,
  USER_ROLE_OPTIONS,
  getRoleLabel,
  getStoredUserRoleFromUserDoc,
  hasSettingsAccess,
} from '../../utils/accessControl';
import { normalizeStoredSubscriptionStatus } from '../../utils/firebase/normalizeSubscriptionStatus';
import { normalizeUserSearchValue } from '../../utils/userSearch';


import './Settings.css';

const DEFAULT_CANCELLATION_FEEDBACK = Object.freeze({
  rating: 5,
  reason: '',
  testimonial: '',
});

const getCallableErrorMessage = (error, fallback) => (
  error?.details?.userMessage ||
  error?.message ||
  fallback
);

const Settings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserDetails, setCurrentUserDetails] = useState({
    email: '',
    uid: '',
    firstName: '',
    lastName: '',
    userRole: 'Not set',
  });

  // Cancel subscription
  const [isCanceling, setIsCanceling] = useState(false);
  const [targetCancelUserId, setTargetCancelUserId] = useState('');

  // Refund payment
  const [isRefunding, setIsRefunding] = useState(false);
  const [targetRefundUserId, setTargetRefundUserId] = useState('');
  const [bankDetailFormVisible, setBankDetailFormVisible] = useState(false);
  const [bankDetailOptions, setBankDetailOptions] = useState([]);
  const [bankDetails, setBankDetails] = useState({
    bank_account_holder: '',
    bank_name: '',
    bank_branch_code: '',
    bank_account_number: '',
    bank_account_type: '',
  });

  // Export Sightings and Count
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [animalFilter, setAnimalFilter] = useState('');
  const [sightingsCount, setSightingsCount] = useState(null);
  const [includeLinks, setIncludeLinks] = useState(false);

  // Change user's email
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  // Ban user
  const [banUserId, setBanUserId] = useState('');
  const [banReason, setBanReason] = useState('');
  const [isBanningUser, setIsBanningUser] = useState(false);

  // Delete user
  const [deleteUserId, setDeleteUserId] = useState('');
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  // Merge accounts
  const [oldMergeEmail, setOldMergeEmail] = useState('');
  const [newMergeEmail, setNewMergeEmail] = useState('');
  const [isMerging, setIsMerging] = useState(false);

  // Admin panel role
  const [roleLookupIdentifier, setRoleLookupIdentifier] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [isSavingRole, setIsSavingRole] = useState(false);
  const [isGettingRole, setIsGettingRole] = useState(false);

  // Custom claims
  const [claimsUid, setClaimsUid] = useState('');
  const [adminClaim, setAdminClaim] = useState(false);
  const [premiumClaim, setPremiumClaim] = useState(false);
  const [subscribedClaim, setSubscribedClaim] = useState(false);
  const [isSettingClaims, setIsSettingClaims] = useState(false);

  // Get Custom Claims
  const [isGettingClaims, setIsGettingClaims] = useState(false);

  const user = useAuthStore((state) => state.user);
  const role = useUserProfileStore((state) => state.role);
  const canExportBankAccounts = hasSettingsAccess(role, SETTINGS_SECTION_KEYS.BANK_EXPORT);
  const canExportSightings = hasSettingsAccess(role, SETTINGS_SECTION_KEYS.SIGHTINGS_EXPORT);
  const canUseAdminTools = hasSettingsAccess(role, SETTINGS_SECTION_KEYS.ADMIN_TOOLS);
  const canAssignRoles = hasSettingsAccess(role, SETTINGS_SECTION_KEYS.ROLE_ASSIGNMENT);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const q = query(collection(db, 'users'), where('bankAccountNumber', '>', ''));
        const tasks = [getDocs(q)];

        if (user?.uid) {
          tasks.push(getDoc(doc(db, 'users', user.uid)));
        }

        const [bankAccountsSnapshot, currentUserSnapshot] = await Promise.all(tasks);
        void bankAccountsSnapshot;

        if (currentUserSnapshot?.exists()) {
          const currentUserDoc = currentUserSnapshot.data();
          const currentUserRole = getStoredUserRoleFromUserDoc(currentUserDoc);

          setCurrentUserDetails({
            email: user?.email || currentUserDoc.email || '',
            uid: user?.uid || currentUserDoc.uid || '',
            firstName: currentUserDoc.firstName || '',
            lastName: currentUserDoc.lastName || '',
            userRole: currentUserRole ? getRoleLabel(currentUserRole) : 'Not set',
          });
        } else {
          setCurrentUserDetails({
            email: user?.email || '',
            uid: user?.uid || '',
            firstName: '',
            lastName: '',
            userRole: 'Not set',
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Initialize workbook and worksheet for export
  let workbook = new Excel.Workbook();
  let worksheet = workbook.addWorksheet('your export sheet name');
  worksheet.columns = [
    { header: 'First Name', key: 'firstName' },
    { header: 'Last Name', key: 'lastName' },
    { header: 'Email', key: 'email' },
    { header: 'Bank Name', key: 'bankAccountName' },
    { header: 'Account Number', key: 'bankAccountNumber' },
    { header: 'Branch Code', key: 'bankBranchCode' },
    { header: 'Paypal', key: 'paypalEmail' },
    { header: 'User Id', key: 'uid' },
  ];
  worksheet.getRow(1).font = { bold: true };

  const getData = async () => {
    let list = [];
    const q = query(collection(db, 'users'), where('bankAccountNumber', '>', ''));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      list.push({ ...doc.data(), id: doc.id });
    });
    const q2 = query(collection(db, 'users'), where('paypalEmail', '>', ''));
    const querySnapshot2 = await getDocs(q2);
    querySnapshot2.forEach((doc) => {
      list.push({ ...doc.data(), id: doc.id });
    });
    const uniqueIds = [...new Set(list.map((item) => item.id))];
    const uniqueList = uniqueIds.map((id) => list.find((item) => item.id === id));
    uniqueList.forEach((item) => {
      worksheet.addRow({ ...item });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const fileExtension = '.xlsx';
    const blob = new Blob([buffer], { type: fileType });
    saveAs(blob, 'fileName' + fileExtension);
  };

  // Initialize Functions
  const functions = getFunctions();

  const getUserDocByIdentifier = async (identifier) => {
    const trimmedIdentifier = String(identifier || '').trim();
    const normalizedIdentifier = normalizeUserSearchValue(trimmedIdentifier);

    if (!trimmedIdentifier) {
      throw new Error('Please enter a User ID / email.');
    }

    const directDocRef = doc(db, 'users', trimmedIdentifier);
    const directDocSnapshot = await getDoc(directDocRef);
    if (directDocSnapshot.exists()) {
      return { ref: directDocRef, snapshot: directDocSnapshot };
    }

    const lookupQueries = [];
    if (trimmedIdentifier.includes('@')) {
      lookupQueries.push(query(collection(db, 'users'), where('email', '==', trimmedIdentifier), limit(1)));
      lookupQueries.push(query(collection(db, 'users'), where('searchEmail', '==', normalizedIdentifier), limit(1)));
    }
    lookupQueries.push(query(collection(db, 'users'), where('uid', '==', trimmedIdentifier), limit(1)));

    for (const lookupQuery of lookupQueries) {
      const querySnapshot = await getDocs(lookupQuery);
      if (!querySnapshot.empty) {
        return {
          ref: querySnapshot.docs[0].ref,
          snapshot: querySnapshot.docs[0],
        };
      }
    }

    if (trimmedIdentifier.includes('@')) {
      const allUsersSnapshot = await getDocs(collection(db, 'users'));
      const matchedDoc = allUsersSnapshot.docs.find((userDoc) =>
        normalizeUserSearchValue(userDoc.data()?.email) === normalizedIdentifier
      );

      if (matchedDoc) {
        return {
          ref: matchedDoc.ref,
          snapshot: matchedDoc,
        };
      }
    }

    throw new Error(`No user found for "${trimmedIdentifier}".`);
  };

  const handleUpdateUserRole = async () => {
    if (!roleLookupIdentifier) {
      alert('Please enter a User ID / email.');
      return;
    }
    if (!selectedRole) {
      alert('Please select an admin panel role.');
      return;
    }

    setIsSavingRole(true);
    try {
      const { ref } = await getUserDocByIdentifier(roleLookupIdentifier);
      await updateDoc(ref, {
        userRole: selectedRole,
        updatedTime: serverTimestamp(),
      });
      alert(`Admin panel role updated to ${getRoleLabel(selectedRole)}.`);
    } catch (err) {
      console.error('Error updating admin panel role:', err);
      alert(err.message || 'Error updating admin panel role.');
    } finally {
      setIsSavingRole(false);
    }
  };

  const handleGetUserRole = async () => {
    if (!roleLookupIdentifier) {
      alert('Please enter a User ID / email.');
      return;
    }

    setIsGettingRole(true);
    try {
      const { snapshot } = await getUserDocByIdentifier(roleLookupIdentifier);
      const currentRole = getStoredUserRoleFromUserDoc(snapshot.data());
      setSelectedRole(currentRole || '');
      alert(`User Role: ${currentRole ? getRoleLabel(currentRole) : 'Not set'}`);
    } catch (err) {
      console.error('Error fetching admin panel role:', err);
      alert(err.message || 'Error fetching admin panel role.');
    } finally {
      setIsGettingRole(false);
    }
  };

  // Cancel Subscription
  const handleCancelSubscription = async () => {
    if (!targetCancelUserId) {
      alert('Please enter a User ID / email.');
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to cancel the subscription for User ID / email "${targetCancelUserId}"?`
    );
    if (!confirmed) return;
    const cancelPayfastSubscription = httpsCallable(functions, 'payfast-cancelUserSubscription');
    setIsCanceling(true);
    try {
      const result = await cancelPayfastSubscription({
        userId: targetCancelUserId,
        ...DEFAULT_CANCELLATION_FEEDBACK,
      });
      await normalizeStoredSubscriptionStatus(targetCancelUserId);
      console.log('Subscription canceled:', result.data);
      alert(result.data.message || 'Subscription canceled successfully.');
    } catch (err) {
      console.error('Error canceling subscription:', err);
      alert(getCallableErrorMessage(err, 'Error canceling subscription.'));
    } finally {
      setIsCanceling(false);
    }
  };

  // Refund Payment
  const handleRefundPayment = async () => {
    if (!targetRefundUserId) {
      alert('Please enter a User ID / email.');
      return;
    }

    const confirmed = window.confirm(`Refund last payment for User ID / email "${targetRefundUserId}"?`);
    if (!confirmed) return;

    const refundPayment = httpsCallable(functions, 'payfast-refundTransaction');
    setIsRefunding(true);
    try {
      const result = await refundPayment({ userId: targetRefundUserId });
      const { data } = result;

      if (data.requiresBankDetails) {
        setBankDetailFormVisible(true);
        setBankDetailOptions(data.bankNames);
        alert("Bank details are required. Please fill the form.");
      } else {
        alert(data.message || 'Refund successful.');
      }
    } catch (err) {
      console.error('Error refunding transaction:', err);
      alert(err.message || 'Error refunding transaction.');
    } finally {
      setIsRefunding(false);
    }
  };

  const handleSubmitBankDetails = async () => {
    const refundPayment = httpsCallable(functions, 'payfast-refundTransaction');
    setIsRefunding(true);
    try {
      const result = await refundPayment({
        userId: targetRefundUserId,
        bankDetails,
      });
      alert(result.data.message || 'Refund successful with bank payout.');
      setBankDetailFormVisible(false);
    } catch (err) {
      console.error('Error processing bank refund:', err);
      alert(err.message || 'Error processing refund.');
    } finally {
      setIsRefunding(false);
    }
  };



  // Change User's Email
  const handleEmailChange = async () => {
    if (!currentEmail || !newEmail) {
      alert('Please enter both the current email and the new email.');
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to change email from "${currentEmail}" to "${newEmail}"?`
    );
    if (!confirmed) return;
    setIsChangingEmail(true);
    try {
      const updateUserEmail = httpsCallable(functions, 'userManagement-changeEmails');
      const result = await updateUserEmail({ oldEmail: currentEmail, newEmail: newEmail });
      console.log('Email updated:', result.data);
      alert(result.data.message || 'Email updated successfully.');
    } catch (err) {
      console.error('Error updating email:', err);
      alert(err.message || 'Error updating email.');
    } finally {
      setIsChangingEmail(false);
    }
  };

  // Ban User
  const handleBanUser = async () => {
    const normalizedUserId = banUserId.trim();
    const normalizedReason = banReason.trim();

    if (!normalizedUserId) {
      alert('Please enter a User ID / email to ban.');
      return;
    }

    const confirmed = window.confirm(
      `Ban the user "${normalizedUserId}"?\n\n` +
      'This will disable their login, cancel future subscription renewal, permanently remove their comments, ' +
      'and mark all their sightings as deleted. Their account will not be deleted.'
    );
    if (!confirmed) return;

    setIsBanningUser(true);
    try {
      const banUser = httpsCallable(functions, 'userManagement-banUser');
      const result = await banUser({
        userId: normalizedUserId,
        reason: normalizedReason,
      });
      const resultData = result.data || {};
      const subscriptionMessage = resultData.subscriptionCancelled
        ? 'Subscription renewal stopped.'
        : 'No managed subscription renewal needed cancellation.';

      alert([
        resultData.message || 'User banned successfully.',
        `Sightings marked deleted: ${resultData.postsSoftDeleted || 0}`,
        `Comments removed: ${resultData.commentsRemoved || 0}`,
        `Comment activities removed: ${resultData.activitiesDeleted || 0}`,
        subscriptionMessage,
      ].join('\n'));
      setBanUserId('');
      setBanReason('');
    } catch (err) {
      console.error('Error banning user:', err);
      const failedSteps = Array.isArray(err?.details?.failedSteps)
        ? err.details.failedSteps.map((step) => step.step).filter(Boolean)
        : [];
      const failedStepMessage = failedSteps.length
        ? `\nFailed cleanup steps: ${failedSteps.join(', ')}`
        : '';
      alert(`${getCallableErrorMessage(err, 'Error banning user.')}${failedStepMessage}`);
    } finally {
      setIsBanningUser(false);
    }
  };

  // Delete User
  const handleDeleteUser = async () => {
    if (!deleteUserId) {
      alert('Please enter a User ID / email to delete.');
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to delete the user with ID / email "${deleteUserId}"? This action cannot be undone!`
    );
    if (!confirmed) return;
    setIsDeletingUser(true);
    try {
      const cancelPayfastSubscription = httpsCallable(functions, 'payfast-cancelUserSubscription');
      try {
        const cancelResult = await cancelPayfastSubscription({
          userId: deleteUserId,
          ...DEFAULT_CANCELLATION_FEEDBACK,
        });
        await normalizeStoredSubscriptionStatus(deleteUserId);
        console.log('Subscription canceled:', cancelResult.data);
      } catch (error) {
        console.warn('Error canceling subscription:', getCallableErrorMessage(error, 'Error canceling subscription.'));
      }
      const deleteUser = httpsCallable(functions, 'userManagement-deleteUser');
      const deleteResult = await deleteUser({ userId: deleteUserId });
      console.log('Delete user result:', deleteResult.data);
      alert(deleteResult.data.message || 'User deleted successfully.');
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err.message || 'Error deleting user.');
    } finally {
      setIsDeletingUser(false);
    }
  };

  // Merge Accounts
  const handleMergeAccounts = async () => {
    if (!oldMergeEmail || !newMergeEmail) {
      alert('Please enter both the old user email and the new user email.');
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to merge the account for "${oldMergeEmail}" into "${newMergeEmail}"?`
    );
    if (!confirmed) return;
    setIsMerging(true);
    try {
      const mergeAccountsFn = httpsCallable(functions, 'userManagement-mergeAccounts');
      const result = await mergeAccountsFn({ oldEmail: oldMergeEmail, newEmail: newMergeEmail });
      console.log('Merge accounts result:', result.data);
      alert(result.data.message || 'Accounts merged successfully.');
    } catch (err) {
      console.error('Error merging accounts:', err);
      alert(err.message || 'Error merging accounts.');
    } finally {
      setIsMerging(false);
    }
  };

  // Set Custom Claims
  const handleSetCustomClaims = async () => {
    if (!claimsUid) {
      alert('Please enter a User ID / email for claims update.');
      return;
    }
    console.log('Setting custom claims for user:', claimsUid);
    console.log('Admin:', adminClaim);
    console.log('Premium Subscriber:', premiumClaim);
    console.log('Subscribed Yearly:', subscribedClaim);
    setIsSettingClaims(true);
    try {
      const setClaims = httpsCallable(functions, 'userManagement-setCustomClaims');
      const result = await setClaims({
        uid: claimsUid,
        admin: adminClaim,
        isPremiumSubscriber: premiumClaim,
        subscribedYearly: subscribedClaim,
      });
      console.log('Custom claims updated:', result.data);
      alert(result.data.message || 'Custom claims updated successfully.');
    } catch (err) {
      console.error('Error updating claims:', err);
      alert(err.message || 'Error updating claims.');
    } finally {
      setIsSettingClaims(false);
    }
  };

  // Get Current Custom Claims & Email Verification
  const handleGetCustomClaims = async () => {
    if (!claimsUid) {
      alert('Please enter a User ID / email to fetch claims.');
      return;
    }
    setIsGettingClaims(true);
    try {
      const getClaims = httpsCallable(functions, 'userManagement-getCustomClaims');
      const result = await getClaims({ uid: claimsUid });
      const currentClaims = result.data.customClaims || {};
      setAdminClaim(Boolean(currentClaims.admin));
      setPremiumClaim(Boolean(currentClaims.isPremiumSubscriber));
      setSubscribedClaim(Boolean(currentClaims.subscribedYearly));
      console.log('Fetched claims:', result.data);
      alert(
        `Custom Claims: ${JSON.stringify(currentClaims)}\n` +
        `Email Verified: ${result.data.emailVerified ? "Yes" : "No"}`
      );
    } catch (err) {
      console.error('Error fetching custom claims:', err);
      alert(err.message || 'Error fetching custom claims.');
    } finally {
      setIsGettingClaims(false);
    }
  };

  const getSightingsExportColumns = () => (
    [
      { header: 'Animal', key: 'animal' },
      { header: 'Individuals Seen', key: 'number' },
      { header: 'Post Time', key: 'postTime' },
      { header: 'Ting Time', key: 'tingTime' },
      { header: 'User', key: 'userId' },
      { header: 'Username', key: 'username' },
      { header: 'Likes', key: 'likesCount' },
      { header: 'Road', key: 'roadname' },
      { header: 'Location', key: 'locationDescription' },
      { header: 'Ting Description', key: 'tingDescription' },
      { header: 'Content Type', key: 'contentType' },
      { header: 'Park', key: 'park' },
      { header: 'Longitude', key: 'longitude' },
      { header: 'Latitude', key: 'latitude' },
      { header: 'Map Accuracy', key: 'accuracy' },
      includeLinks ? { header: 'Photo Link', key: 'postImg' } : false,
      includeLinks ? { header: 'Video Link', key: 'postVideo' } : false,
      { header: 'Post ID', key: 'id' },
    ].filter(Boolean)
  );

  const buildSightingExportRow = (docSnapshot) => {
    const data = docSnapshot.data();
    const row = {
      id: docSnapshot.id,
      animal: data.animal ?? '',
      number: data.number ?? '',
      postTime: data.postTime instanceof Date ? data.postTime : data.postTime?.toDate?.(),
      tingTime: data.tingTime instanceof Date ? data.tingTime : data.tingTime?.toDate?.(),
      userId: data.userId ?? '',
      username: data.adminTingedForWho ?? data.userData?.username ?? '',
      likesCount: Array.isArray(data.likes) ? data.likes.length : 0,
      roadname: data.roadname ?? '',
      locationDescription: data.locationDescription ?? '',
      tingDescription: data.tingDescription ?? '',
      contentType: data.contentType ?? '',
      park: data.park ?? '',
      longitude: data.coordinates?.longitude ?? '',
      latitude: data.coordinates?.latitude ?? '',
      accuracy: data.accuracy ?? '',
    };

    if (includeLinks) {
      row.postImg = data.postImg ?? '';
      row.postVideo = data.postVideo ?? '';
    }

    return row;
  };

  // Export all sightings within a date range
  const handleExportSightings = async () => {
    if (!startDate || !endDate) {
      alert('Please select start and end dates.');
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const wb = new Excel.Workbook();
    const ws = wb.addWorksheet('Sightings');
    ws.columns = getSightingsExportColumns();
    ws.getRow(1).font = { bold: true };

    const q = query(
      collection(db, 'posts'),
      where('deletedTime', '==', null),
      where('postTime', '>=', start),
      where('postTime', '<=', end)
    );

    const snapshot = await getDocs(q);
    snapshot.forEach((docSnapshot) => {
      ws.addRow(buildSightingExportRow(docSnapshot));
    });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'sightings.xlsx');
  };

  // Export animal specific sightings within a date range
  const handleExportAnimalSightings = async () => {
    if (!animalFilter) {
      alert('Please enter an animal.');
      return;
    }
    if (!startDate || !endDate) {
      alert('Please select start and end dates.');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const wb = new Excel.Workbook();
    const ws = wb.addWorksheet('Sightings');
    ws.columns = getSightingsExportColumns();
    ws.getRow(1).font = { bold: true };

    const q = query(
      collection(db, 'posts'),
      where('deletedTime', '==', null),
      where('animal', '==', animalFilter),
      where('postTime', '>=', start),
      where('postTime', '<=', end)
    );

    const snapshot = await getDocs(q);
    snapshot.forEach((docSnapshot) => {
      ws.addRow(buildSightingExportRow(docSnapshot));
    });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${animalFilter}-sightings.xlsx`);
  };

  // Get count of sightings within a date range
  const handleGetSightingsCount = async () => {
    if (!startDate || !endDate) {
      alert('Please select start and end dates.');
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const q = query(
      collection(db, 'posts'),
      where('deletedTime', '==', null),
      where('postTime', '>=', start),
      where('postTime', '<=', end)
    );
    try {
      const snapshot = await getCountFromServer(q);
      setSightingsCount(snapshot.data().count);
    } catch (err) {
      console.error('Error getting count:', err);
      alert(err.message || 'Error fetching count');
    }
  };

  return (
    <div className="account__container">
      <h1 className="account__heading" style={{ fontWeight: 'bold' }}>Settings</h1>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </div>
      ) : (
          <>
          <div className="section-item account__current-user">
            <h3 style={{ fontWeight: 'bold' }}>Current Logged In User</h3>
            <div className="account__current-user-grid">
              <div>
                <span>Email</span>
                <strong>{currentUserDetails.email || 'Not available'}</strong>
              </div>
              <div className="account__current-user-item--uid">
                <span>UID</span>
                <strong className="account__current-user-value--uid">{currentUserDetails.uid || 'Not available'}</strong>
              </div>
              <div>
                <span>Name</span>
                <strong>{currentUserDetails.firstName || 'Not available'}</strong>
              </div>
              <div>
                <span>Surname</span>
                <strong>{currentUserDetails.lastName || 'Not available'}</strong>
              </div>
              <div>
                <span>User Role</span>
                <strong>{currentUserDetails.userRole}</strong>
              </div>
            </div>
          </div>
          <div className="section-grid">
            {canExportBankAccounts && (
              <div className="section-item">
                <h3 style={{ fontWeight: 'bold' }}>Export Bank Accounts</h3>
                <Button variant="contained" onClick={getData} style={{ marginTop: '1rem' }}>
                  Export Bank Accounts
                </Button>
              </div>
            )}

            {canExportSightings && (
              <div className="section-item">
                <h3 style={{ fontWeight: 'bold' }}>Export Sightings</h3>
                <label htmlFor="startDate">Start Date:</label>
                <input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <label htmlFor="endDate">End Date:</label>
                <input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <label htmlFor="animalFilter">Animal (optional):</label>
                <input
                  id="animalFilter"
                  type="text"
                  value={animalFilter}
                  onChange={(e) => setAnimalFilter(e.target.value)}
                  placeholder="Animal..."
                />
                <div style={{ marginTop: '10px' }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={includeLinks}
                      onChange={(e) => setIncludeLinks(e.target.checked)}
                    />{' '}
                    Include content links
                  </label>
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleExportSightings}
                  style={{ marginTop: '10px' }}
                >
                  Export All Sightings
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleExportAnimalSightings}
                  style={{ marginTop: '10px' }}
                >
                  Export Animal Sightings
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGetSightingsCount}
                  style={{ marginTop: '10px' }}
                >
                  Get Sightings Count
                </Button>
                {sightingsCount !== null && (
                  <p>Number of Sightings: {sightingsCount}</p>
                )}
              </div>
            )}

            {bankDetailFormVisible && (
              <div className="bank-detail-form">
                <h4>Bank Details Required</h4>
                <input placeholder="Account Holder" value={bankDetails.bank_account_holder}
                  onChange={(e) => setBankDetails({ ...bankDetails, bank_account_holder: e.target.value })} />
                <select value={bankDetails.bank_name}
                  onChange={(e) => setBankDetails({ ...bankDetails, bank_name: e.target.value })}>
                  <option value="">Select Bank</option>
                  {bankDetailOptions.map((bank) => (
                    <option key={bank.bank_name} value={bank.bank_name}>
                      {bank.label}
                    </option>
                  ))}
                </select>
                <input placeholder="Branch Code" value={bankDetails.bank_branch_code}
                  onChange={(e) => setBankDetails({ ...bankDetails, bank_branch_code: e.target.value })} />
                <input placeholder="Account Number" value={bankDetails.bank_account_number}
                  onChange={(e) => setBankDetails({ ...bankDetails, bank_account_number: e.target.value })} />
                <select value={bankDetails.bank_account_type}
                  onChange={(e) => setBankDetails({ ...bankDetails, bank_account_type: e.target.value })}>
                  <option value="">Account Type</option>
                  <option value="CURRENT">Current</option>
                  <option value="SAVINGS">Savings</option>
                  <option value="TRANSMISSION">Transmission</option>
                </select>
                <Button onClick={handleSubmitBankDetails} variant="contained" color="primary" style={{ marginTop: '10px' }}>
                  Submit Refund with Bank Details
                </Button>
              </div>
            )}

            {canUseAdminTools && (
              <>
                <div className="section-item">
                  <h3 style={{ fontWeight: 'bold' }}>Cancel User Subscription</h3>
                  <input
                    id="cancelSubscriptionUserId"
                    type="text"
                    value={targetCancelUserId}
                    onChange={(e) => setTargetCancelUserId(e.target.value)}
                    placeholder="Enter user ID / email..."
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleCancelSubscription}
                    disabled={isCanceling}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}
                  >
                    {isCanceling ? (
                      <>
                        <CircularProgress size={20} />
                        Canceling...
                      </>
                    ) : (
                      'Cancel Subscription'
                    )}
                  </Button>
                </div>

                <div className="section-item">
                  <h3 style={{ fontWeight: 'bold' }}>Refund last subscription payment</h3>
                  <input
                    id="refundPaymentUserId"
                    type="text"
                    value={targetRefundUserId}
                    onChange={(e) => setTargetRefundUserId(e.target.value)}
                    placeholder="Enter user ID / email..."
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleRefundPayment}
                    disabled={isRefunding}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}
                  >
                    {isRefunding ? (
                      <>
                        <CircularProgress size={20} />
                        Refunding...
                      </>
                    ) : (
                      'Refund'
                    )}
                  </Button>
                </div>

                <div className="section-item">
                  <h3 style={{ fontWeight: 'bold' }}>Change a user's email</h3>
                  <label htmlFor="currentEmail">Current email:</label>
                  <input
                    id="currentEmail"
                    type="text"
                    value={currentEmail}
                    onChange={(e) => setCurrentEmail(e.target.value)}
                    placeholder="Current email..."
                  />
                  <label htmlFor="newEmail">New email:</label>
                  <input
                    id="newEmail"
                    type="text"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="New email..."
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleEmailChange}
                    disabled={isChangingEmail}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}
                  >
                    {isChangingEmail ? (
                      <>
                        <CircularProgress size={20} />
                        Updating...
                      </>
                    ) : (
                      'Change Email'
                    )}
                  </Button>
                </div>

                <div className="section-item account__ban-user-card">
                  <h3 style={{ fontWeight: 'bold' }}>Ban a User</h3>
                  <p className="account__ban-warning">
                    Disables login without deleting the account. Comments are permanently removed and sightings are marked deleted.
                  </p>
                  <label htmlFor="banUserId">User ID / email:</label>
                  <input
                    id="banUserId"
                    type="text"
                    value={banUserId}
                    onChange={(e) => setBanUserId(e.target.value)}
                    placeholder="Enter user ID / email..."
                  />
                  <label htmlFor="banReason">Reason (optional):</label>
                  <textarea
                    id="banReason"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Why is this account being banned?"
                    maxLength={500}
                    rows={3}
                  />
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleBanUser}
                    disabled={isBanningUser}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}
                  >
                    {isBanningUser ? (
                      <>
                        <CircularProgress size={20} color="inherit" />
                        Banning...
                      </>
                    ) : (
                      'Ban User'
                    )}
                  </Button>
                </div>

                <div className="section-item">
                  <h3 style={{ fontWeight: 'bold' }}>Delete a User</h3>
                  <label htmlFor="deleteUserId">User ID / email:</label>
                  <input
                    id="deleteUserId"
                    type="text"
                    value={deleteUserId}
                    onChange={(e) => setDeleteUserId(e.target.value)}
                    placeholder="Enter user ID / email..."
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleDeleteUser}
                    disabled={isDeletingUser}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}
                  >
                    {isDeletingUser ? (
                      <>
                        <CircularProgress size={20} />
                        Deleting...
                      </>
                    ) : (
                      'Delete User'
                    )}
                  </Button>
                </div>

                <div className="section-item">
                  <h3 style={{ fontWeight: 'bold' }}>Merge Accounts</h3>
                  <label htmlFor="oldMergeEmail">Old Account Email:</label>
                  <input
                    id="oldMergeEmail"
                    type="text"
                    value={oldMergeEmail}
                    onChange={(e) => setOldMergeEmail(e.target.value)}
                    placeholder="Old user email..."
                  />
                  <label htmlFor="newMergeEmail">New Account Email:</label>
                  <input
                    id="newMergeEmail"
                    type="text"
                    value={newMergeEmail}
                    onChange={(e) => setNewMergeEmail(e.target.value)}
                    placeholder="New user email..."
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleMergeAccounts}
                    disabled={isMerging}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}
                  >
                    {isMerging ? (
                      <>
                        <CircularProgress size={20} />
                        Merging...
                      </>
                    ) : (
                      'Merge'
                    )}
                  </Button>
                </div>
              </>
            )}

            {canAssignRoles && (
              <div className="section-item">
                <h3 style={{ fontWeight: 'bold' }}>Assign User Role</h3>
                <label htmlFor="roleLookupIdentifier">User ID / email:</label>
                <input
                  id="roleLookupIdentifier"
                  type="text"
                  value={roleLookupIdentifier}
                  onChange={(e) => setRoleLookupIdentifier(e.target.value)}
                  placeholder="Enter user ID / email..."
                />
                <label htmlFor="roleClaim" style={{ marginTop: '10px' }}>Admin panel role:</label>
                <select
                  id="roleClaim"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <option value="">Select a role...</option>
                  {USER_ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p style={{ marginTop: '10px', color: 'var(--color-muted)' }}>
                  User, Guide, and Contributor are app-only. They cannot enter the admin panel.
                </p>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleUpdateUserRole}
                  disabled={isSavingRole}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}
                >
                  {isSavingRole ? (
                    <>
                      <CircularProgress size={20} />
                      Updating...
                    </>
                  ) : (
                    'Update Role'
                  )}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGetUserRole}
                  disabled={isGettingRole}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}
                >
                  {isGettingRole ? (
                    <>
                      <CircularProgress size={20} />
                      Fetching Role...
                    </>
                  ) : (
                    'Show Current Role'
                  )}
                </Button>
              </div>
            )}

            {canUseAdminTools && (
              <div className="section-item">
                <h3 style={{ fontWeight: 'bold' }}>Set Custom Claims</h3>
                <label htmlFor="claimsUid">User ID / email:</label>
                <input
                  id="claimsUid"
                  type="text"
                  value={claimsUid}
                  onChange={(e) => setClaimsUid(e.target.value)}
                  placeholder="Enter user ID / email..."
                />
                <div style={{ marginTop: '10px' }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={adminClaim}
                      onChange={(e) => setAdminClaim(e.target.checked)}
                    />{' '}
                    Admin
                  </label>
                </div>
                <div>
                  <label>
                    <input
                      type="checkbox"
                      checked={premiumClaim}
                      onChange={(e) => setPremiumClaim(e.target.checked)}
                    />{' '}
                    Premium Subscriber
                  </label>
                </div>
                <div>
                  <label>
                    <input
                      type="checkbox"
                      checked={subscribedClaim}
                      onChange={(e) => setSubscribedClaim(e.target.checked)}
                    />{' '}
                    Subscribed Yearly
                  </label>
                </div>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSetCustomClaims}
                  disabled={isSettingClaims}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}
                >
                  {isSettingClaims ? (
                    <>
                      <CircularProgress size={20} />
                      Updating...
                    </>
                  ) : (
                    'Update Claims'
                  )}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGetCustomClaims}
                  disabled={isGettingClaims}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}
                >
                  {isGettingClaims ? (
                    <>
                      <CircularProgress size={20} />
                      Fetching Claims...
                    </>
                  ) : (
                    'Show Current Claims'
                  )}
                </Button>
              </div>
            )}
          </div>
          </>
      )}
    </div>
  );
};

export default Settings;
